/**
 * AdminOperativo.js - Centro de Control Operativo
 * Vista centrada en Usuarios → Tags
 */
import React, { useState, useEffect, useCallback } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { initFirebase } from "../../firebaseConfig";
import { signInWithGoogle, signOutUser } from "../../services/api/modules/auth";

import "bootstrap/dist/css/bootstrap.min.css";
import "./../AdminOperativo/AdminOperativo.css";

// Componentes existentes
import ActionHistory from "./../AdminOperativo/components/ActionHistory";
import BatchCreateModal from "./../components/BatchCreateModal";
import BuildInfo from "./../components/BuildInfo";

// Nuevos componentes
import UsuariosView from "./components/UsuariosView";
import TagsSearchView from "./components/TagsSearchView";
import UserTagsView from "./components/UserTagsView";
import VeterinariosView from "./components/VeterinariosView";
import ProveedoresView from "./components/ProveedoresView";
import CredencialesView from "./components/CredencialesView";
import PlantillasManager from "./components/PlantillasManager";

// Directorio
import { DirectorioList, DirectorioTipoSelect, DirectorioForm } from "../directorio";

// Hooks
import { useMetrics } from "./hooks/useMetrics";
import { useActionHistory } from "./hooks/useActionHistory";
import { useUsuarios } from "./hooks/useUsuarios";
import useSearch from "./hooks/useSearch";

function AdminOperativo() {
  // Estado de autenticación
  const [adminUser, setAdminUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Estado de vistas
  const [activeView, setActiveView] = useState("usuarios");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  // Estado de Directorio
  const [directorioSubview, setDirectorioSubview] = useState('list'); // 'list', 'tipo-select', 'form'
  const [directorioTipoNuevo, setDirectorioTipoNuevo] = useState(null);
  const [directorioNegocioSeleccionado, setDirectorioNegocioSeleccionado] = useState(null);
  
  // Hooks
  const { loadMetrics, loading: metricsLoading } = useMetrics();
  const { history, addToHistory } = useActionHistory(adminUser);
  const { usuarios, loading: usuariosLoading, loadUsuarios, loadObjetosUsuario } = useUsuarios();
  const { searchData, loading: searchLoading, performSearch } = useSearch();

  // URLs base
  const isProduction = process.env.NODE_ENV === "production";
  const baseURL = isProduction
    ? process.env.REACT_APP_BASE_URL
    : process.env.REACT_APP_API_URL || "http://localhost:3000";

  // Auth setup
  useEffect(() => {
    let unsubscribe;
    
    async function setupAuth() {
      try {
        const { auth } = await initFirebase();
        
        unsubscribe = onAuthStateChanged(auth, (user) => {
          setAdminUser(user);
          setAuthLoading(false);
          
          if (user) {
              loadMetrics(user);
            
            if (activeView === "usuarios") {
              loadUsuarios(1, '');
            }
          }
        });
      } catch (error) {
        console.error('Error inicializando auth:', error);
        setAuthLoading(false);
      }
    }
    
    setupAuth();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Manejadores
  const handleUserSelect = useCallback((userId) => {
    setSelectedUserId(userId);
    setActiveView('userTags');
    addToHistory(`Visualizando tags del usuario ${userId}`);
  }, [addToHistory]);

  const handleViewChange = useCallback((view, filter = null) => {
    setActiveView(view);
    setSelectedUserId(null);
    setActiveFilter(filter);

    // Reset estado de directorio
    if (view === 'directorio') {
      setDirectorioSubview('list');
      setDirectorioTipoNuevo(null);
      setDirectorioNegocioSeleccionado(null);
    }

    if (view === "usuarios") {
      loadUsuarios(1, '');
    }

    addToHistory(`Cambio a vista: ${view}${filter ? ` con filtro ${filter}` : ''}`);
  }, [loadUsuarios, addToHistory]);

  const handleBatchCreated = useCallback(() => {
    setShowBatchModal(false);
    loadMetrics();
    if (activeView === "usuarios") {
      loadUsuarios(1, '');
    }
    addToHistory('Lote de registros creado');
  }, [loadMetrics, loadUsuarios, activeView, addToHistory]);

  // Loading de autenticación
  if (authLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Verificando sesión...</span>
        </div>
        <p className="mt-3">Verificando sesión...</p>
      </div>
    );
  }

  // Sin autenticación
  if (!adminUser) {
    return (
      <div className="container mt-5 text-center">
        <h2 className="mb-3">Panel Operativo - Acceso Restringido</h2>
        <button 
          onClick={() => signInWithGoogle().catch(console.error)} 
          className="btn btn-warning"
        >
          Iniciar Sesión con Google
        </button>
      </div>
    );
  }

  const loading = metricsLoading || usuariosLoading || searchLoading;

  return (
    <div className="admin-operativo min-vh-100 bg-light">
      {/* Indicador de carga */}
      {loading && (
        <div className="position-fixed top-0 start-0 w-100" style={{zIndex: 9999}}>
          <div className="progress" style={{height: '3px'}}>
            <div className="progress-bar progress-bar-striped progress-bar-animated" 
                 role="progressbar" 
                 style={{width: '100%'}}></div>
          </div>
        </div>
      )}

      {/* Header con métricas y navegación */}
      <div className="top-bar bg-white border-bottom sticky-top">
        <div className="container-fluid p-3">
           
          
          {/* Navegación principal */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button 
                  className={`nav-link ${activeView === 'usuarios' ? 'active' : ''}`}
                  onClick={() => handleViewChange('usuarios')}
                >
                  <i className="bi bi-people me-2"></i>
                  Usuarios
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'tags' ? 'active' : ''}`}
                  onClick={() => handleViewChange('tags')}
                >
                  <i className="bi bi-tags me-2"></i>
                  Buscar Tags
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'veterinarios' ? 'active' : ''}`}
                  onClick={() => handleViewChange('veterinarios')}
                >
                  <i className="bi bi-heart-pulse me-2"></i>
                  Veterinarios
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'proveedores' ? 'active' : ''}`}
                  onClick={() => handleViewChange('proveedores')}
                >
                  <i className="bi bi-shop me-2"></i>
                  Proveedores
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'credenciales' ? 'active' : ''}`}
                  onClick={() => handleViewChange('credenciales')}
                >
                  <i className="bi bi-credit-card-2-front me-2"></i>
                  Credenciales
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'plantillas' ? 'active' : ''}`}
                  onClick={() => handleViewChange('plantillas')}
                >
                  <i className="bi bi-envelope-paper me-2"></i>
                  Plantillas
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeView === 'directorio' ? 'active' : ''}`}
                  onClick={() => handleViewChange('directorio')}
                >
                  <i className="bi bi-building me-2"></i>
                  Directorio
                </button>
              </li>
              {selectedUserId && activeView === 'userTags' && (
                <li className="nav-item">
                  <button 
                    className="nav-link active"
                    onClick={() => setActiveView('userTags')}
                  >
                    <i className="bi bi-person-badge me-2"></i>
                    Tags de Usuario
                  </button>
                </li>
              )}
            </ul>
            
            <div className="d-flex gap-2">
              <button
                className="btn btn-success"
                onClick={() => setShowBatchModal(true)}
                disabled={loading}
              >
                <i className="bi bi-plus-circle"></i> Crear Lote
              </button>
              <a href="/admin-ops/solicitudes-upgrade" className="btn btn-outline-success" title="Solicitudes de Upgrade">
                <i className="bi bi-arrow-up-circle"></i>
              </a>
              <a href="/deploy-history" className="btn btn-outline-info" title="Historial de Deploys">
                <i className="bi bi-clock-history"></i>
              </a>
              <a href="/dashboard" className="btn btn-outline-secondary" title="Ir al Dashboard">
                <i className="bi bi-house"></i>
              </a>
              <button onClick={signOutUser} className="btn btn-outline-danger">
                <i className="bi bi-box-arrow-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="main-content-area">
        <div className="container-fluid p-3">
          <div className="row g-3">
            <div className="col-12 col-xl-9">
              {/* Vista de Usuarios */}
              {activeView === 'usuarios' && (
                <UsuariosView 
                  usuarios={usuarios}
                  loading={usuariosLoading}
                  onUserSelect={handleUserSelect}
                  onLoadMore={() => loadUsuarios(2, '')}
                  onSearch={(search) => loadUsuarios(1, search)}
                  onPlanChange={async (userId, newPlan) => {
                    addToHistory(`Plan cambiado a ${newPlan} para usuario ${userId}`);
                  }}
                />
              )}

              {/* Vista de Búsqueda de Tags */}
              {activeView === 'tags' && (
                <TagsSearchView 
                  key={activeFilter || 'all'}
                  searchData={searchData}
                  loading={searchLoading}
                  onSearch={performSearch}
                  baseURL={baseURL}
                  addToHistory={addToHistory}
                  initialFilter={activeFilter}
                />
              )}

              {/* Vista de Tags del Usuario */}
              {activeView === 'userTags' && selectedUserId && (
                <UserTagsView
                  userId={selectedUserId}
                  onBack={() => handleViewChange('usuarios')}
                  baseURL={baseURL}
                  addToHistory={addToHistory}
                  loadObjetosUsuario={loadObjetosUsuario}
                />
              )}

              {/* Vista de Veterinarios */}
              {activeView === 'veterinarios' && (
                <VeterinariosView
                  addToHistory={addToHistory}
                />
              )}

              {/* Vista de Proveedores */}
              {activeView === 'proveedores' && (
                <ProveedoresView
                  addToHistory={addToHistory}
                />
              )}

              {/* Vista de Credenciales */}
              {activeView === 'credenciales' && (
                <CredencialesView
                  addToHistory={addToHistory}
                />
              )}

              {/* Vista de Plantillas de Email */}
              {activeView === 'plantillas' && (
                <PlantillasManager
                  addToHistory={addToHistory}
                />
              )}

              {/* Vista de Directorio */}
              {activeView === 'directorio' && (
                <>
                  {directorioSubview === 'list' && (
                    <DirectorioList
                      onSelectNegocio={(negocio) => {
                        setDirectorioNegocioSeleccionado(negocio);
                        setDirectorioSubview('form');
                      }}
                      onNuevoNegocio={() => {
                        setDirectorioNegocioSeleccionado(null);
                        setDirectorioTipoNuevo(null);
                        setDirectorioSubview('tipo-select');
                      }}
                      addToHistory={addToHistory}
                    />
                  )}
                  {directorioSubview === 'tipo-select' && (
                    <DirectorioTipoSelect
                      onSelectTipo={(tipo) => {
                        setDirectorioTipoNuevo(tipo);
                        setDirectorioSubview('form');
                      }}
                      onVolver={() => setDirectorioSubview('list')}
                    />
                  )}
                  {directorioSubview === 'form' && (
                    <DirectorioForm
                      negocio={directorioNegocioSeleccionado}
                      tipoNegocio={directorioTipoNuevo}
                      onVolver={() => {
                        if (directorioNegocioSeleccionado) {
                          setDirectorioSubview('list');
                        } else {
                          setDirectorioSubview('tipo-select');
                        }
                      }}
                      onGuardado={(result, wasDeleted) => {
                        // Volver a la lista después de guardar o eliminar
                        setDirectorioSubview('list');
                        setDirectorioNegocioSeleccionado(null);
                        setDirectorioTipoNuevo(null);
                      }}
                      addToHistory={addToHistory}
                    />
                  )}
                </>
              )}
            </div>

            {/* Panel lateral - Historial */}
            <div className="col-12 col-xl-3 d-none d-xl-block">
              <ActionHistory history={history} />
            </div>
          </div>
        </div>
      </div>

      {/* Modal de creación en lote */}
      {showBatchModal && (
        <BatchCreateModal
          show={showBatchModal}
          onClose={() => setShowBatchModal(false)}
          onBatchCreated={handleBatchCreated}
        />
      )}

      {/* Build Info - Esquina inferior derecha */}
      <BuildInfo position="fixed" />
    </div>
  );
}

export default AdminOperativo;